import { motion } from 'framer-motion';
import { Linkedin, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { AnyPermissionGate } from '@/modules/auth/components/permissions/AnyPermissionGate';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import type {
  CandidateJobApi,
  CandidateOriginApi,
  CandidateStatusApi,
  CandidateWorkExperienceResponseApi,
} from '@/modules/candidates/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import type { components } from '@/types/apiSchema';

import { CandidateContactStatus } from './CandidateContactStatus';
import { CandidateOriginTag } from './candidateOriginTag/CandidateOriginTag';
import { SkillBadge } from './SkillBadge';
import { WorkExperienceTimeline } from './WorkExperienceTimeline';

type CandidateJobSummaryResponse =
  components['schemas']['CandidateJobSummaryResponse'];

import CandidateStatusTag from './CandidateStatusTag';
import {
  HiringStatusDropdown,
  type HiringStatusDropdownProps,
} from './HiringStatusDropdown';

/**
 * Component hierarchy example:
 *
 * <CandidateCard selected={isSelected}>
 *   <CandidateCardHeader>
 *     <CandidateCardCheckbox
 *       candidateId={candidate.id}
 *       selected={isSelected}
 *       onSelect={handleSelectCandidate}
 *     />
 *     <CandidateCardTitle
 *       jobId={jobId}
 *       candidate={candidate}
 *       candidateStatus={status}
 *     />
 *     <CandidateCardActions>
 *       <CandidateScore score={score} />
 *       <CandidateCardHiringStatus
 *         jobId={jobId}
 *         candidateId={candidate.id}
 *         currentStatus={hiringStatus}
 *         hiringStages={hiringStages}
 *       />
 *       <CandidateActionButton candidateJob={candidateJob} />
 *     </CandidateCardActions>
 *   </CandidateCardHeader>
 *
 *    <CandidateCardContent>
 *      <CandidateCardBackground
 *        workExperiences={workExperiences}
 *        strengths={strengths}
 *        clarifications={clarifications}
 *      />
 *    </CandidateCardContent>
 * </CandidateCard>
 */

interface CandidateCardProps {
  selected?: boolean;
  ineligible?: boolean;
  children: React.ReactNode;
  className?: string;
  animate?: boolean;
}

function CandidateCard({
  selected = false,
  ineligible = false,
  children,
  className,
  animate = true,
}: CandidateCardProps) {
  const content = (
    <div
      className={cn(
        'bg-card rounded-xl border transition-all',
        selected && 'border-l-primary border-l-4 shadow-md',
        ineligible && 'border-red-200 bg-red-50',
        className,
      )}
    >
      {children}
    </div>
  );

  if (animate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {content}
      </motion.div>
    );
  }

  return content;
}

interface CandidateCardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

function CandidateCardHeader({
  children,
  className,
}: CandidateCardHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:gap-4 md:px-6 md:py-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CandidateCardCheckboxProps {
  candidateId: string;
  selected: boolean;
  onSelect: (candidateId: string, checked: boolean) => void;
  disabled?: boolean;
}

function CandidateCardCheckbox({
  candidateId,
  selected,
  onSelect,
  disabled = false,
}: CandidateCardCheckboxProps) {
  const handleCheckboxChange = (checked: boolean) => {
    onSelect(candidateId, checked);
  };

  const checkbox = (
    <Checkbox
      checked={selected}
      onCheckedChange={handleCheckboxChange}
      disabled={disabled}
    />
  );

  return (
    <AnyPermissionGate permissions={[permissions.CandidateJobs.Write]}>
      {checkbox}
    </AnyPermissionGate>
  );
}

interface CandidateCardTitleProps {
  candidate: CandidateJobApi['candidate'];
  candidateStatus: CandidateStatusApi;
  /** Shown after the status when given. */
  candidateOrigin?: CandidateOriginApi;
  className?: string;
  linkTo?: string;
  prioritizeWorkExperience?: boolean;
  statusLabel?: string;
  unifiedPrescreeningStatus?: boolean;
}

interface CandidateCardIdentityProps {
  name: string;
  headline?: string | null;
  supportingText?: string | null;
  location?: string | null;
  badge?: React.ReactNode;
  className?: string;
  linkTo?: string;
  linkedinUrl?: string | null;
}

function extractHeadlineFromWorkExperience(
  candidate: CandidateJobApi['candidate'],
) {
  return candidate.workExperiences?.at(0)?.jobTitle &&
    candidate.workExperiences?.at(0)?.companyName
    ? `${candidate.workExperiences?.at(0)?.jobTitle} at ${candidate.workExperiences?.at(0)?.companyName}`
    : null;
}

function CandidateCardIdentity({
  name,
  headline,
  supportingText,
  location,
  badge,
  className,
  linkTo,
  linkedinUrl,
}: CandidateCardIdentityProps) {
  const subheading = (
    <Subheading className="max-w-40 truncate sm:max-w-[250px] md:max-w-[300px]">
      {name}
    </Subheading>
  );

  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-3', className)}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          {linkTo ? <Link to={linkTo}>{subheading}</Link> : <>{subheading}</>}
          {linkedinUrl && (
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${name} on LinkedIn`}
              title="Open LinkedIn profile"
              className="text-primary hover:text-primary/80 inline-flex"
            >
              <Linkedin className="size-4" />
            </a>
          )}
          {badge}
        </div>

        {(headline || location) && (
          <div className="flex items-center gap-1">
            {headline && (
              <Paragraph
                variant="caption"
                className="line-clamp-1 max-w-[160px] truncate sm:max-w-[250px] md:max-w-[400px]"
              >
                {headline}
              </Paragraph>
            )}
            {headline && location && (
              <Paragraph variant="caption" className="shrink-0">
                |
              </Paragraph>
            )}
            {location && (
              <Paragraph variant="caption" className="truncate">
                {location}
              </Paragraph>
            )}
          </div>
        )}
        {supportingText && (
          <Paragraph
            variant="caption"
            className="line-clamp-1 max-w-[160px] truncate text-[11px] font-normal text-neutral-500 sm:max-w-[250px] md:max-w-[400px]"
          >
            {supportingText}
          </Paragraph>
        )}
      </div>
    </div>
  );
}

function CandidateCardTitle({
  candidate,
  candidateStatus,
  candidateOrigin,
  className,
  linkTo,
  prioritizeWorkExperience = false,
  statusLabel,
  unifiedPrescreeningStatus = false,
}: CandidateCardTitleProps) {
  const workExperienceHeadline = extractHeadlineFromWorkExperience(candidate);
  const headline =
    prioritizeWorkExperience && workExperienceHeadline
      ? workExperienceHeadline
      : candidate.headline || workExperienceHeadline;
  const supportingText =
    prioritizeWorkExperience &&
    workExperienceHeadline &&
    candidate.headline !== workExperienceHeadline
      ? candidate.headline
      : null;

  return (
    <CandidateCardIdentity
      name={`${candidate.firstName} ${candidate.lastName}`}
      headline={headline}
      supportingText={supportingText}
      location={candidate.location}
      className={className}
      linkTo={linkTo}
      badge={
        <>
          <CandidateStatusTag
            label={statusLabel}
            status={candidateStatus}
            source={candidate.source}
            unifiedPrescreeningStatus={unifiedPrescreeningStatus}
          />
          {candidateOrigin && <CandidateOriginTag origin={candidateOrigin} />}
        </>
      }
    />
  );
}

interface CandidateCardActionsProps {
  children: React.ReactNode;
  className?: string;
}

function CandidateCardActions({
  children,
  className,
}: CandidateCardActionsProps) {
  return (
    <div
      className={cn(
        'flex w-full shrink-0 items-center justify-between gap-2 md:ml-auto md:w-auto md:gap-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

type CandidateCardHiringStatus = HiringStatusDropdownProps;

function CandidateCardHiringStatus({
  jobId,
  candidateId,
  currentStatus,
  hiringStages,
}: HiringStatusDropdownProps) {
  return (
    <PermissionsGate permissions={[permissions.CandidateJobs.Write]}>
      <HiringStatusDropdown
        jobId={jobId}
        candidateId={candidateId}
        currentStatus={currentStatus}
        hiringStages={hiringStages}
      />
    </PermissionsGate>
  );
}

interface CandidateCardContentProps {
  children: React.ReactNode;
  className?: string;
}

function CandidateCardContent({
  children,
  className,
}: CandidateCardContentProps) {
  return (
    <div className={cn('px-4 py-4 md:px-6 md:py-5', className)}>{children}</div>
  );
}

interface CandidateCardBackgroundProps {
  workExperiences?: CandidateWorkExperienceResponseApi[] | null;
  skills?: string[] | null;
  strengths?: CandidateJobSummaryResponse[] | null;
  clarifications?: CandidateJobSummaryResponse[] | null;
  defaultExpanded?: boolean;
}

function CandidateCardBackground({
  workExperiences,
  skills,
  strengths,
  clarifications,
  defaultExpanded,
}: CandidateCardBackgroundProps) {
  const experiences = workExperiences ?? [];
  const hasStrengths = strengths && strengths.length > 0;
  const hasClarifications = clarifications && clarifications.length > 0;
  const hasStrengthsOrClarifications = hasStrengths || hasClarifications;
  const hasWorkExperiences = experiences.length > 0;
  const visibleSkills = (skills ?? []).filter(Boolean).slice(0, 12);
  const hasSkills = visibleSkills.length > 0;
  const hasRightColumnContent = hasSkills || hasStrengthsOrClarifications;

  return (
    <div
      className={cn(
        'flex flex-col gap-6',
        hasWorkExperiences && hasRightColumnContent && 'md:flex-row md:gap-8',
      )}
    >
      {hasWorkExperiences && (
        <div className="flex-1">
          <WorkExperienceTimeline
            workExperiences={experiences}
            defaultExpanded={defaultExpanded}
          />
        </div>
      )}

      {hasWorkExperiences && hasRightColumnContent && (
        <div className="bg-border -my-4 hidden w-px self-stretch md:-my-5 md:block" />
      )}

      {hasRightColumnContent ? (
        <div className="flex-1">
          {hasSkills && (
            <div className="mb-4 flex flex-wrap gap-2">
              {visibleSkills.map((skill) => (
                <Badge key={skill} variant="outline" size="sm">
                  {skill}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {strengths?.map((strength, index) => (
              <SkillBadge
                key={`strength-${index}`}
                isMustHave
                summary={strength.summary}
                explanation={strength.explanation}
              />
            ))}
            {clarifications?.map((clarification, index) => (
              <SkillBadge
                key={`clarification-${index}`}
                isMustHave={false}
                summary={clarification.summary}
                explanation={clarification.explanation}
              />
            ))}
          </div>
        </div>
      ) : !hasWorkExperiences ? (
        <Paragraph variant="caption">
          No strengths or clarifications available
        </Paragraph>
      ) : null}
    </div>
  );
}

type CandidateJobFeedbackResponseApi =
  components['schemas']['CandidateJobFeedbackResponse'];

interface CandidateCardHMFeedbackBadgeProps {
  hmFeedbacks: CandidateJobFeedbackResponseApi[];
}

function CandidateCardHMFeedbackBadge({
  hmFeedbacks,
}: CandidateCardHMFeedbackBadgeProps) {
  const lastFeedback = hmFeedbacks[0];
  if (!lastFeedback) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        lastFeedback.isPositive
          ? 'bg-semantic-560/10 text-semantic-560'
          : 'bg-semantic-550/10 text-semantic-550',
      )}
    >
      {lastFeedback.isPositive ? (
        <ThumbsUp className="size-3" />
      ) : (
        <ThumbsDown className="size-3" />
      )}
      HM Feedback
    </div>
  );
}

type ReachOutMessageApi = NonNullable<CandidateJobApi['reachOutMessage']>;

interface CandidateCardReachOutMessageProps {
  jobId: string;
  reachOutMessage: ReachOutMessageApi;
}

function CandidateCardReachOutMessage({
  jobId,
  reachOutMessage,
}: CandidateCardReachOutMessageProps) {
  return (
    <CandidateContactStatus jobId={jobId} reachOutMessage={reachOutMessage} />
  );
}

export {
  CandidateCard,
  CandidateCardActions,
  CandidateCardBackground,
  CandidateCardCheckbox,
  CandidateCardContent,
  CandidateCardHeader,
  CandidateCardHiringStatus,
  CandidateCardHMFeedbackBadge,
  CandidateCardIdentity,
  CandidateCardReachOutMessage,
  CandidateCardTitle,
};
